import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../api/axiosConfig";
import { toast } from "react-toastify";
import { useAuth } from "./AuthContext";

const InventoryContext = createContext();
export const useInventory = () => useContext(InventoryContext);

export const InventoryProvider = ({ children }) => {
  const [inventories, setInventories] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [stockHistory, setStockHistory] = useState([]);
  const [scrapList, setScrapList] = useState([]); 
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  // =================================================================
  // 1. DATA FETCHING (SYNC WITH SQL)
  // =================================================================
  const fetchData = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const [invRes, whRes, histRes, scrapRes] = await Promise.all([
        api.get('/inventory/products').catch(() => ({ data: [] })),
        api.get('/inventory/warehouses').catch(() => ({ data: [] })),
        api.get('/inventory/history').catch(() => ({ data: [] })),
        api.get('/scrap-orders').catch(() => ({ data: [] })) 
      ]);

      const productsData = Array.isArray(invRes.data) ? invRes.data : (invRes.data?.data || []);
      const whData = Array.isArray(whRes.data) ? whRes.data : (whRes.data?.data || []);
      const scrapData = Array.isArray(scrapRes.data) ? scrapRes.data : (scrapRes.data?.data || []);
      const historyData = Array.isArray(histRes.data) ? histRes.data : (histRes.data?.data || []);

      setInventories(productsData.map(i => ({ 
          ...i, 
          itemName: i.itemName || i.item_name || "Unnamed Item" 
      })));
      setWarehouses(whData);
      setScrapList(scrapData); 
      
      const mappedHistory = historyData.map(h => ({
        id: h.id,
        timestamp: h.move_date,
        type: h.move_type,
        itemName: h.item_name,
        quantity: h.quantity,
        note: h.notes,
        warehouseId: h.warehouse_id,
        bin: h.bin_name,
        from: h.move_type === 'IN' ? 'External' : h.warehouse_name,
        to: h.move_type === 'IN' ? h.warehouse_name : 'Project/Scrap'
      }));
      setStockHistory(mappedHistory);

    } catch (err) {
      console.error("Error loading inventory:", err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { 
    if (!user || !user.id) {
      setInventories([]);
      setWarehouses([]);
      setStockHistory([]);
      setScrapList([]);
    }
  }, [user]);

  // =================================================================
  // 2. CORE ACTIONS
  // =================================================================
  const getInventory = useCallback((id) => {
    return inventories.find(i => String(i.id) === String(id));
  }, [inventories]);

  const fetchItemById = useCallback(async (id) => {
    if (!id) return null;
    try {
      const res = await api.get(`/inventory/products/${id}`);
      return res.data?.data || res.data;
    } catch (err) {
      console.error("fetchItemById error:", err);
      return null;
    }
  }, []);

  const saveInventory = async (data) => {
    try {
      let res;
      if (data.id) {
        res = await api.put(`/inventory/products/${data.id}`, data);
      } else {
        res = await api.post('/inventory/products', data);
      }
      await fetchData();
      return res.data?.data || res.data;
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save product");
      throw err;
    }
  };

  const deleteInventory = async (id) => {
    if(!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/inventory/products/${id}`);
      toast.success("Item deleted");
      await fetchData();
    } catch (err) {
      toast.error("Cannot delete item (Check history/dependencies)");
    }
  };

  const saveWarehouse = async (data) => {
    try {
      await api.post('/inventory/warehouses', data);
      await fetchData();
    } catch (err) {
      toast.error("Failed to save warehouse");
    }
  };

  const addBinToWarehouse = async (warehouseId, binName) => {
    try {
      await api.post('/inventory/bins', { warehouseId, binName });
      await fetchData();
    } catch (err) {
      toast.error("Failed to add bin");
    }
  };

  const addStock = async (itemId, qty, warehouseId, bin, note, source) => {
    try {
        await api.post('/inventory/move', {
            productId: itemId, warehouseId, binName: bin, type: 'IN', quantity: qty, referenceType: 'ADJUSTMENT', notes: note
        });
        fetchData();
        return true;
    } catch (err) {
        toast.error(err.response?.data?.error || "Movement Failed");
        return false;
    }
  };

  const removeStock = async (itemId, qty, warehouseId, bin, note, destination) => {
    try {
        await api.post('/inventory/move', {
            productId: itemId, warehouseId, binName: bin, type: 'OUT', quantity: qty, referenceType: 'ADJUSTMENT', notes: `${note} (To: ${destination})`
        });
        fetchData();
        return true;
    } catch (err) {
        toast.error(err.response?.data?.error || "Insufficient Stock");
        return false;
    }
  };

  // =================================================================
  // 3. SCRAP ORDER WORKFLOW (ISOLATED PATHS)
  // =================================================================
  const createScrapOrder = async (scrapData) => {
    try {
      const res = await api.post('/scrap-orders', scrapData);
      await fetchData();
      return { success: true, id: res.data?.id || res.data?.data?.id, reference: res.data?.reference }; 
    } catch (err) {
      return { success: false, message: err.response?.data?.error || "Failed to create scrap order" };
    }
  };

  const updateRequisitionStatus = async (id, status) => {
    try {
      await api.put(`/scrap-orders/${id}/status`, { status });
      await fetchData();
      return { success: true };
    } catch (err) {
      return { success: false, message: "Update Failed" };
    }
  };

  const validateScrapRequisition = async (id) => {
    try {
      await api.post(`/scrap-orders/${id}/validate`, {});
      await fetchData();
      return { success: true };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.response?.data?.error || "Validation failed" };
    }
  };

  // =================================================================
  // 4. TRANSFERS & ISSUANCE
  // =================================================================
  const processGoodsReceipt = async (receiptData) => {
    try {
        await api.post('/inventory/receipt', receiptData);
        fetchData();
        return { success: true };
    } catch (err) {
        return { success: false, message: err.response?.data?.error || "Receipt Failed" };
    }
  };

  const transferStock = async (transferData) => {
    const { sourceWarehouseId, sourceBin, destWarehouseId, destBin, items, reference } = transferData;
    try {
        for (let item of items) {
             await api.post('/inventory/move', { productId: item.productId, warehouseId: sourceWarehouseId, binName: sourceBin, type: 'OUT', quantity: item.quantity, referenceType: 'TRANSFER', notes: `Transfer Out: ${reference}` });
             await api.post('/inventory/move', { productId: item.productId, warehouseId: destWarehouseId, binName: destBin, type: 'IN', quantity: item.quantity, referenceType: 'TRANSFER', notes: `Transfer In: ${reference}` });
        }
        fetchData();
        return { success: true, message: "Transfer Complete" };
    } catch (err) {
         return { success: false, message: "Transfer Failed" };
    }
  };

  const transferToProject = async (productId, quantity, projectName, requisitionId, projectId) => {
    const product = inventories.find(i => String(i.id) === String(productId));
    if (!product || product.totalQuantity < quantity) return { success: false, message: "Insufficient Stock" };
    const location = product.locations?.find(l => l.qty >= quantity);
    if (!location) return { success: false, message: "No single bin has enough stock. Please split issuance." };

    try {
        await api.post('/inventory/issue', {
            projectId, taskId: null, warehouseId: location.warehouseId, binId: location.binId, materialId: productId, quantity: quantity, notes: `Requisition: ${requisitionId}`, userId: 1 
        });
        fetchData();
        return { success: true };
    } catch (err) {
        return { success: false, message: err.response?.data?.error || "Issuance Failed" };
    }
  };

  // =================================================================
  // 5. GETTERS & UTILS & SCRAP ACTIONS
  // =================================================================
  const getWarehouse = (id) => warehouses.find(w => String(w.id) === String(id));
  const getInventoryByWarehouse = (whId) => inventories.filter(item => item.locations?.some(loc => String(loc.warehouseId) === String(whId)));
  const getTotalStock = (id) => { const item = getInventory(id); return item ? item.totalQuantity : 0; };
  
  const getScrapStock = (id) => { 
      const item = getInventory(id); 
      return item ? (Number(item.scrap_quantity) || 0) : 0; 
  };

  const sellScrap = async (saleData) => {
      try {
          await api.post('/scrap-orders/sale', saleData);
          await fetchData();
          return { success: true };
      } catch (err) {
          return { success: false, message: err.response?.data?.message || err.response?.data?.error || "Sale failed" };
      }
  };

  const reuseScrap = async (recoverData) => {
      try {
          await api.post('/scrap-orders/recover', recoverData);
          await fetchData();
          return { success: true };
      } catch (err) {
          return { success: false, message: err.response?.data?.message || err.response?.data?.error || "Recovery failed" };
      }
  };

  return (
    <InventoryContext.Provider value={{
        inventories, warehouses, stockHistory, scrapList, loading, fetchData, fetchInventory: fetchData,
        saveInventory, deleteInventory, getInventory,
        saveWarehouse, getWarehouse, getInventoryByWarehouse, addBinToWarehouse,
        addStock, removeStock, transferStock,
        transferToProject, processGoodsReceipt,
        createScrapOrder, updateRequisitionStatus, validateScrapRequisition,
        sellScrap, reuseScrap,
        getTotalStock, getScrapStock, materialCategories: ["Cement", "Steel", "Aggregates", "General"]
    }}>
      {children}
    </InventoryContext.Provider>
  );
};